import React, { useState, useEffect } from "react";
import {
  App, Table, Card, Input, Select, Space, Tag, Button,
  Row, Col, Statistic, Tooltip, Checkbox, Badge, Typography
} from "antd";
import {
  SearchOutlined, ReloadOutlined, DisconnectOutlined, UserOutlined
} from "@ant-design/icons";
import { useAuth } from "../../hooks/useAuth";
import { PermissionButton } from "../common/PermissionWrapper";
import { PERMISSIONS } from "../../utils/permissions";
import apiService from "../../services/api";
import { formatDateTime } from "../../utils/formatters";
import "./OnlineUsers.css";

const { Text, Title } = Typography;
const { Option } = Select;

const OnlineUsers = () => {
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [onlineStats, setOnlineStats] = useState({ count: 2, timestamp: "" });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(5000); // 5 seconds

  const { modal, message } = App.useApp();
  const { user: currentLoggedUser } = useAuth();

  useEffect(() => {
    loadOnlineUsers();
  }, [pagination.current, pagination.pageSize, searchTerm]);

  useEffect(() => {
    let interval = null;
    if (autoRefresh) {
      interval = setInterval(() => {
        loadOnlineUsers(false);
      }, refreshInterval);
    }
    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, pagination.current, pagination.pageSize, searchTerm]);

  const loadOnlineUsers = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const params = {
        page: pagination.current,
        page_size: pagination.pageSize,
        ...(searchTerm && { search: searchTerm }),
      };

      const response = await apiService.online.getOnlineUsers(params);
      if (!response.data.success) {
        throw new Error(response.data.message || "获取在线用户失败");
      }

      const users = response.data.data?.list || [];
      const total = response.data.data?.total || 0;
      setOnlineUsers(users);
      setPagination(prev => ({ ...prev, total }));

      // Load stats with the total as fallback
      loadOnlineStats(total);
    } catch (err) {
      message.error(
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message ||
        "加载在线用户失败"
      );
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const loadOnlineStats = async (totalFromList = undefined) => {
    try {
      const response = await apiService.online.getOnlineStats();
      if (response.data && response.data.success) {
        const stats = response.data.data || { count: 0, timestamp: "" };
        // Overwrite the count with the more accurate table list total if available
        if (totalFromList !== undefined) {
          stats.count = Math.max(stats.count || 0, totalFromList);
        }
        setOnlineStats(stats);
        return;
      }
    } catch (err) {
      // background silent load
    }

    // Fallback: if stats API fails or is not supported, use the list total
    if (totalFromList !== undefined) {
      setOnlineStats(prev => ({ ...prev, count: totalFromList }));
    }
  };

  const handleForceLogout = async (userId, username) => {
    modal.confirm({
      title: "强制退出用户",
      content: `确定要强制退出用户 "${username}" 吗？这将结束该用户的所有会话。`,
      okText: "确定",
      cancelText: "取消",
      okType: "danger",
      onOk: async () => {
        try {
          const response = await apiService.online.forceLogoutUser(userId);
          if (!response.data.success) {
            throw new Error(response.data.message || "强制退出用户失败");
          }
          message.success(`用户 "${username}" 已被强制退出`);
          loadOnlineUsers();
          loadOnlineStats();
        } catch (err) {
          message.error(
            err.response?.data?.error?.message ||
            err.response?.data?.message ||
            err.message ||
            "强制退出用户失败"
          );
        }
      },
    });
  };


  const formatDuration = (startTime) => {
    if (!startTime) return "未知";
    const now = new Date();
    const start = new Date(startTime);
    if (isNaN(start.getTime())) return "未知";
    const diffMs = now - start;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "刚刚";
    if (diffMins < 60) return `${diffMins} 分钟前`;
    if (diffHours < 24) return `${diffHours} 小时前`;
    return `${diffDays} 天前`;
  };

  const getUserStatus = (lastActivityTime) => {
    if (!lastActivityTime) return "offline";
    const now = new Date();
    const lastActivity = new Date(lastActivityTime);
    if (isNaN(lastActivity.getTime())) return "offline";

    const diffMs = now - lastActivity;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 5) return "active"; // < 5 mins
    if (diffMins < 15) return "away"; // < 15 mins
    return "offline"; // > 15 mins
  };

  const isCurrentUser = (userId) => {
    return currentLoggedUser?.id === userId;
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const columns = [
    {
      title: "状态",
      dataIndex: "last_seen",
      key: "status",
      width: 50,
      align: "center",
      render: (lastSeen) => {
        const status = getUserStatus(lastSeen);
        const map = {
          active: { status: "success", text: "活跃" },
          away: { status: "warning", text: "离开" },
          offline: { status: "default", text: "离线" },
        };
        const config = map[status];
        return (
          <Tooltip title={config.text}>
            <span>
              <Badge status={config.status} />
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "用户信息",
      key: "user_info",
      width: 150,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600, color: "var(--color-text)" }}>
            {record.username}
            {isCurrentUser(record.id) && (
              <Tag color="blue" style={{ marginLeft: 8 }}>
                当前
              </Tag>
            )}
          </div>
          <div style={{ color: "var(--color-text-secondary)", fontSize: 13 }}>
            {record.email}
          </div>
        </div>
      ),
    },
    {
      title: "角色",
      dataIndex: "role",
      key: "role",
      width: 60,
      render: (role) => (
        <Tag color={role === "admin" ? "orange" : "default"}>
          {role === "admin" ? "管理员" : "普通用户"}
        </Tag>
      ),
    },
    {
      title: "IP地址",
      dataIndex: "ip_address",
      key: "ip_address",
      width: 100,
      render: (ip) => (
        <span
          style={{
            fontFamily: "var(--font-mono)",
            background: "var(--color-bg-elevated)",
            padding: "2px 6px",
            borderRadius: "4px",
            color: "var(--color-text-secondary)",
            border: "1px solid var(--color-border)",
          }}
        >
          {ip || "未知"}
        </span>
      ),
    },
    {
      title: "用户代理",
      dataIndex: "user_agent",
      key: "user_agent",
      width: 120,
      render: (agent) => (
        <Tooltip placement="topLeft" title={agent}>
          <span style={{ color: "var(--color-text-secondary)" }}>
            {agent || "未知"}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "最后活跃",
      dataIndex: "last_seen",
      key: "last_seen_time",
      width: 180,
      render: (time) => (
        <div>
          <div style={{ fontWeight: 500, color: "var(--color-text)" }}>
            {formatDuration(time)}
          </div>
          <div style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>
            {formatDateTime(time)}
          </div>
        </div>
      ),
    },
    {
      title: "会话时长",
      dataIndex: "last_seen",
      key: "duration",
      width: 120,
      render: (time) => (
        <span style={{ color: "var(--color-text-secondary)" }}>
          {formatDuration(time)}
        </span>
      ),
    },
    {
      title: "操作",
      key: "action",
      width: 140,
      render: (_, record) => {
        if (isCurrentUser(record.id)) {
          return (
            <span style={{ color: "var(--color-text-disabled)", fontSize: 13 }}>
              -
            </span>
          );
        }
        return (
          <PermissionButton
            permission={PERMISSIONS.FORCE_LOGOUT}
            type="link"
            danger
            icon={<DisconnectOutlined />}
            onClick={() => handleForceLogout(record.id, record.username)}
          >
            踢下线
          </PermissionButton>
        );
      },
    },
  ];

  return (
    <div className="online-users-page">
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card className="stat-card">
            <Statistic
              title="当前在线用户"
              value={onlineStats.count}
              prefix={<UserOutlined />}
              valueStyle={{ color: "var(--color-success)" }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title="在线用户管理"
        extra={
          <div style={{ fontSize: 13, color: "var(--color-text-tertiary)" }}>
            上次更新: {formatDateTime(onlineStats.timestamp)}
          </div>
        }
      >
        <Space wrap size="middle" style={{ marginBottom: 16 }}>
          <Input
            placeholder="搜索用户名或邮箱..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onPressEnter={() => setPagination({ ...pagination, current: 1 })}
            prefix={<SearchOutlined />}
            style={{ width: 250 }}
            allowClear
          />

          <Checkbox
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          >
            自动刷新
          </Checkbox>
          {autoRefresh && (
            <Select
              value={refreshInterval}
              onChange={setRefreshInterval}
              style={{ width: 100 }}
              size="small"
            >
              <Option value={3000}>3秒</Option>
              <Option value={5000}>5秒</Option>
              <Option value={10000}>10秒</Option>
              <Option value={30000}>30秒</Option>
            </Select>
          )}
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              loadOnlineUsers(true);
              loadOnlineStats();
            }}
          >
            刷新
          </Button>
        </Space>

        <Table
          columns={columns}
          dataSource={onlineUsers}
          rowKey="id"
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 人在线`,
          }}
          loading={loading}
          onChange={handleTableChange}
          size="middle"
          rowClassName={(record) =>
            isCurrentUser(record.id) ? "current-user-row" : ""
          }
        />
      </Card>
    </div>
  );
};

export default OnlineUsers;
